import {
  parseTerms,
  validateAudienceAssignments,
  validateConversationTheme,
  type Bookmark,
} from "./domain";
import type {
  BookmarkRepository,
  OwnerBookmarkWorkspace,
  SummaryEnrichmentJob,
} from "./repository";

export interface BulkCleanupPatch {
  primaryTheme: string | null;
  addTags: string[];
  removeTags: string[];
  audienceID: string | null;
  destination: "keep" | "cards" | "library" | "both" | "remove";
  makePrimary: boolean;
}

export interface BulkCleanupFailure {
  bookmarkID: string;
  message: string;
}

export interface BulkCleanupSaveResult {
  workspace: OwnerBookmarkWorkspace;
  completedBookmarkIDs: string[];
  unchangedBookmarkIDs: string[];
  needsReviewBookmarks: BulkCleanupFailure[];
  notAttemptedBookmarkIDs: string[];
  failure: BulkCleanupFailure | null;
  summaryEnrichmentJobs: SummaryEnrichmentJob[];
}

export interface BulkCleanupEvaluation {
  outcome: "changed" | "unchanged" | "needs_review";
  bookmark: Bookmark;
  message: string | null;
}

class BulkCleanupNeedsReviewError extends Error {}

function activeDestination(
  destination: BulkCleanupPatch["destination"],
): { conversationEnabled: boolean; libraryEnabled: boolean } | null {
  switch (destination) {
    case "keep":
      return null;
    case "cards":
      return { conversationEnabled: true, libraryEnabled: false };
    case "library":
      return { conversationEnabled: false, libraryEnabled: true };
    case "both":
      return { conversationEnabled: true, libraryEnabled: true };
    case "remove":
      return { conversationEnabled: false, libraryEnabled: false };
  }
}

function updatedAudienceState(
  bookmark: Bookmark,
  patch: BulkCleanupPatch,
  editedAt: string,
): Pick<Bookmark, "audienceAssignments" | "libraryPublications"> {
  const changesDestination = patch.destination !== "keep";
  if (!patch.audienceID && (changesDestination || patch.makePrimary)) {
    throw new Error("Choose an audience before changing its destination.");
  }
  if (!patch.audienceID) {
    return {
      audienceAssignments: bookmark.audienceAssignments,
      libraryPublications: bookmark.libraryPublications,
    };
  }
  const target = bookmark.audienceAssignments.find(
    ({ audienceId }) => audienceId === patch.audienceID,
  );
  if (!target) {
    throw new BulkCleanupNeedsReviewError(
      "The selected audience is no longer available for this bookmark.",
    );
  }
  const destination = activeDestination(patch.destination);
  const targetFlags = destination ?? {
    conversationEnabled: target.conversationEnabled,
    libraryEnabled: target.libraryEnabled,
  };
  if (
    patch.makePrimary
    && !targetFlags.conversationEnabled
    && !targetFlags.libraryEnabled
  ) {
    throw new BulkCleanupNeedsReviewError(
      "A removed audience cannot become the default audience.",
    );
  }
  if (targetFlags.libraryEnabled) {
    const livePublications = bookmark.libraryPublications.filter(
      ({ assignmentId, revokedAt }) => (
        assignmentId === target.id && revokedAt === null
      ),
    );
    if (
      livePublications.length !== 1
      || !livePublications[0].sharedTitle.trim()
      || !livePublications[0].sharedSourceLabel.trim()
    ) {
      throw new BulkCleanupNeedsReviewError(
        "Open this bookmark to add explicit Shared Library details first.",
      );
    }
  }

  let assignments = bookmark.audienceAssignments.map((assignment) => (
    assignment.id === target.id
      ? {
        ...assignment,
        ...targetFlags,
        isPrimary: patch.makePrimary ? true : assignment.isPrimary,
      }
      : {
        ...assignment,
        isPrimary: patch.makePrimary ? false : assignment.isPrimary,
      }
  ));
  const activeAssignments = assignments.filter(
    ({ conversationEnabled, libraryEnabled }) => (
      conversationEnabled || libraryEnabled
    ),
  );
  if (!activeAssignments.length) {
    throw new BulkCleanupNeedsReviewError(
      "Keep at least one Cards or Library destination.",
    );
  }
  const primary = assignments.find(({ isPrimary }) => isPrimary);
  if (
    !primary
    || (!primary.conversationEnabled && !primary.libraryEnabled)
  ) {
    const replacement = activeAssignments[0];
    assignments = assignments.map((assignment) => ({
      ...assignment,
      isPrimary: assignment.id === replacement.id,
    }));
  }
  const assignmentError = validateAudienceAssignments(assignments);
  if (assignmentError) {
    throw new BulkCleanupNeedsReviewError(assignmentError);
  }
  const targetStillUsesLibrary = assignments.find(
    ({ id }) => id === target.id,
  )?.libraryEnabled ?? false;
  const publications = targetStillUsesLibrary
    ? bookmark.libraryPublications
    : bookmark.libraryPublications.map((publication) => (
      publication.assignmentId === target.id && publication.revokedAt === null
        ? {
          ...publication,
          revokedAt: editedAt,
          updatedAt: editedAt,
        }
        : publication
    ));
  return {
    audienceAssignments: assignments,
    libraryPublications: publications,
  };
}

export function applyBulkCleanupPatch(
  bookmark: Bookmark,
  patch: BulkCleanupPatch,
  editedAt = new Date().toISOString(),
): Bookmark {
  const primaryTheme = patch.primaryTheme?.trim() || bookmark.primaryTheme;
  const themeError = validateConversationTheme(primaryTheme);
  if (themeError) throw new Error(themeError);
  const removedTags = new Set(
    parseTerms(patch.removeTags.join(",")).map((tag) => (
      tag.toLocaleLowerCase("en")
    )),
  );
  const tags = parseTerms([
    ...bookmark.tags.filter((tag) => (
      !removedTags.has(tag.toLocaleLowerCase("en"))
    )),
    ...patch.addTags,
  ].join(","));
  const audienceState = updatedAudienceState(bookmark, patch, editedAt);
  return {
    ...bookmark,
    primaryTheme,
    tags,
    ...audienceState,
  };
}

export function evaluateBulkCleanupPatch(
  bookmark: Bookmark,
  patch: BulkCleanupPatch,
): BulkCleanupEvaluation {
  try {
    const updated = applyBulkCleanupPatch(bookmark, patch);
    const changed = (
      updated.primaryTheme !== bookmark.primaryTheme
      || JSON.stringify(updated.tags) !== JSON.stringify(bookmark.tags)
      || JSON.stringify(updated.audienceAssignments)
        !== JSON.stringify(bookmark.audienceAssignments)
      || JSON.stringify(updated.libraryPublications)
        !== JSON.stringify(bookmark.libraryPublications)
    );
    return {
      outcome: changed ? "changed" : "unchanged",
      bookmark: changed ? updated : bookmark,
      message: null,
    };
  } catch (error) {
    if (!(error instanceof BulkCleanupNeedsReviewError)) throw error;
    return {
      outcome: "needs_review",
      bookmark,
      message: error.message,
    };
  }
}

export function bulkCleanupChangesBookmark(
  bookmark: Bookmark,
  patch: BulkCleanupPatch,
): boolean {
  return evaluateBulkCleanupPatch(bookmark, patch).outcome === "changed";
}

export async function saveBulkCleanup(
  repository: BookmarkRepository,
  initialWorkspace: OwnerBookmarkWorkspace,
  bookmarkIDs: string[],
  patch: BulkCleanupPatch,
): Promise<BulkCleanupSaveResult> {
  let workspace = structuredClone(initialWorkspace);
  const pendingIDs = [...new Set(bookmarkIDs)];
  const completedBookmarkIDs: string[] = [];
  const unchangedBookmarkIDs: string[] = [];
  const needsReviewBookmarks: BulkCleanupFailure[] = [];
  const summaryEnrichmentJobs: SummaryEnrichmentJob[] = [];

  for (const [index, bookmarkID] of pendingIDs.entries()) {
    const bookmark = workspace.bookmarks.find(({ id }) => id === bookmarkID);
    if (!bookmark) {
      return {
        workspace,
        completedBookmarkIDs,
        unchangedBookmarkIDs,
        needsReviewBookmarks,
        notAttemptedBookmarkIDs: pendingIDs.slice(index + 1),
        failure: {
          bookmarkID,
          message: "The selected bookmark is no longer in this workspace.",
        },
        summaryEnrichmentJobs,
      };
    }
    const evaluation = evaluateBulkCleanupPatch(bookmark, patch);
    if (evaluation.outcome === "needs_review") {
      needsReviewBookmarks.push({
        bookmarkID,
        message: evaluation.message ?? "Open this bookmark to review it.",
      });
      continue;
    }
    if (evaluation.outcome === "unchanged") {
      unchangedBookmarkIDs.push(bookmarkID);
      continue;
    }
    try {
      const result = await repository.saveBookmark(evaluation.bookmark);
      workspace = result.workspace;
      completedBookmarkIDs.push(bookmarkID);
      if (result.summaryEnrichmentJob) {
        summaryEnrichmentJobs.push(result.summaryEnrichmentJob);
      }
    } catch (error) {
      return {
        workspace,
        completedBookmarkIDs,
        unchangedBookmarkIDs,
        needsReviewBookmarks,
        notAttemptedBookmarkIDs: pendingIDs.slice(index + 1),
        failure: {
          bookmarkID,
          message: error instanceof Error
            ? error.message
            : "The private bookmark could not save.",
        },
        summaryEnrichmentJobs,
      };
    }
  }

  return {
    workspace,
    completedBookmarkIDs,
    unchangedBookmarkIDs,
    needsReviewBookmarks,
    notAttemptedBookmarkIDs: [],
    failure: null,
    summaryEnrichmentJobs,
  };
}
